import moment from 'moment';

import { getTimeIntervals } from 'Helpers/timeIntervals';

// Above this many period columns the Days axis is coarsened to Months. Unlike
// the /plan Gantt (which windows and renders only the visible columns), this
// report axis renders EVERY column as a flex cell, so the ceiling is the point
// where day cells become too thin to read — about a quarter's worth of days.
// A low ceiling also keeps coarsening consistent under a category filter: a
// ~1-year category (~365 days) coarsens just like the full multi-year plan,
// instead of squishing hundreds of unreadable day columns because its narrower
// range slipped under a far higher cap.
export const MAX_AXIS_COLUMNS = 92;

/**
 * The dates that place an activity row on a time axis, parsed with moment so a
 * date-only value is never timezone-shifted.
 */
const rowDates = (row) => [row.start_date, row.target_completion_date, row.estimated_completion_date].filter(Boolean).map((date) => moment(date));

/**
 * The min/max date across every non-group row, or null when no row carries a
 * date at all (a fully "undated" set).
 */
export const computeDateBounds = (rows) => {
    const dates = [];

    (rows || []).forEach((row) => {
        if (!row._is_group) {
            dates.push(...rowDates(row));
        }
    });

    if (dates.length === 0) {
        return null;
    }

    return { start: moment.min(dates), end: moment.max(dates) };
};

/**
 * Flatten getTimeIntervals() into an ordered list of period columns
 * ({ key, label, start, end }) for the requested view. Reuses the /plan
 * roadmap period engine so the report axis matches the plan module exactly.
 */
export const buildPeriodColumns = (bounds, period) => {
    if (!bounds) {
        return [];
    }

    const intervals = getTimeIntervals(bounds.start, bounds.end, period);
    const years = intervals.years || {};
    const columns = [];

    Object.keys(years)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((year) => {
            const yearData = years[year];
            const shortYear = String(year).slice(2);

            if (period === 'years') {
                columns.push({ key: String(year), label: String(year), start: yearData.start, end: yearData.end });
                return;
            }

            if (period === 'quarters') {
                Object.values(yearData.quarters || {}).forEach((quarter) => {
                    columns.push({ key: quarter.key, label: `${quarter.label} '${shortYear}`, start: quarter.start, end: quarter.end });
                });
                return;
            }

            Object.keys(yearData.months || {})
                .map(Number)
                .sort((a, b) => a - b)
                .forEach((monthNumber) => {
                    const month = yearData.months[monthNumber];

                    if (period === 'days') {
                        Object.keys(month.days || {})
                            .map(Number)
                            .sort((a, b) => a - b)
                            .forEach((dayNumber) => {
                                const day = month.days[dayNumber];
                                columns.push({ key: day.key, label: day.label, start: day.start, end: day.end });
                            });
                        return;
                    }

                    columns.push({ key: month.key, label: `${month.label.slice(0, 3)} '${shortYear}`, start: month.start, end: month.end });
                });
        });

    return columns;
};

/**
 * Build the period-column axis for a view, coarsening a Days axis to Months
 * when it would exceed MAX_AXIS_COLUMNS. Single source of truth shared by the
 * timeline/gantt views (which render the columns) and ProjectPlanReportTable
 * (which renders the coarsening notice OUTSIDE the table frame).
 */
export const buildAxis = (bounds, period) => {
    const requested = period || 'months';
    const columns = buildPeriodColumns(bounds, requested);

    if (requested === 'days' && columns.length > MAX_AXIS_COLUMNS) {
        return { columns: buildPeriodColumns(bounds, 'months'), coarsened: true };
    }

    return { columns, coarsened: false };
};

/**
 * A left-edge offset (0–100%) for a date along the [axisStart, axisEnd] span,
 * clamped so an out-of-window date sticks to an edge rather than overflowing.
 */
export const offsetPercent = (date, bounds) => {
    if (!date || !bounds) {
        return 0;
    }

    const total = bounds.end.diff(bounds.start);

    if (total <= 0) {
        return 0;
    }

    const pct = (moment(date).diff(bounds.start) / total) * 100;

    return Math.min(100, Math.max(0, pct));
};

/**
 * Walk the flat row list (group-header rows interleaved with activity rows)
 * into swim-lane sections: [{ label, rows: [...] }]. Activity rows before any
 * header fall into a single unlabelled leading section.
 */
export const groupRowsIntoSections = (rows) => {
    const sections = [];
    let current = null;

    (rows || []).forEach((row) => {
        if (row._is_group) {
            current = { label: row.group, rows: [] };
            sections.push(current);
            return;
        }

        if (!current) {
            current = { label: '', rows: [] };
            sections.push(current);
        }

        current.rows.push(row);
    });

    return sections;
};

/**
 * The RAG classification for an activity row: overdue always reads red,
 * otherwise the stored rag_status ('red' | 'amber' | 'green') drives it.
 * 'amber' has no project token, so the default Tailwind yellow-400 is used —
 * the same choice ProjectPlanReportTableRow makes for its RAG dot.
 */
export const ragTone = (row) => {
    if (row.is_overdue || row.rag_status === 'red') {
        return 'red';
    }

    if (row.rag_status === 'amber') {
        return 'amber';
    }

    if (row.rag_status === 'green') {
        return 'green';
    }

    return 'neutral';
};
